// biome-ignore-all lint/suspicious/noConsole: SSE stream error reporting until Sentry SDK is wired up
import { extractSSEErrorMessage } from '@utils/extractSSEErrorMessage';
import { normalizeRole } from '@utils/roleUtils';
import { useCallback, useEffect, useRef } from 'react';
import { API_BASE, SENTINEL_SSE_EVENTS, SSE_CONFIG } from 'src/constants/sentinel';
import {
	addOrUpdateTimelineEvent,
	addTimelineEvent,
	processCompleted,
	respondPhaseActionsReceived,
	respondPhaseFailed,
	respondPhaseRiskAnalysisReceived,
	respondPhaseStarted
} from 'src/store/slices/sentinel';
import { useAppDispatch, useAppSelector } from 'universal-scripts';

export function useStreamRespond(
	verifyStatus: 'idle' | 'processing' | 'completed' | 'error',
	lang: string = 'en',
	refreshCache: boolean = false
) {
	const dispatch = useAppDispatch();
	const verifications = useAppSelector((state) => state.sentinel.phases.verify.verifications);
	const processId = useAppSelector((state) => state.sentinel.processId);
	const status = useAppSelector((state) => state.sentinel.phases.respond.status);

	const eventSourceRef = useRef<EventSource | null>(null);
	const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);
	const retryCountRef = useRef(0);

	const isMountedRef = useRef(false);
	const isCompletedRef = useRef(false);
	const hasStartedRef = useRef(false);
	const currentProcessIdRef = useRef<string | null>(null);

	const cleanup = useCallback(() => {
		isMountedRef.current = false;
		isCompletedRef.current = true;
		abortControllerRef.current?.abort();
		abortControllerRef.current = null;
		if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
		if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
		if (eventSourceRef.current) {
			eventSourceRef.current.close();
			eventSourceRef.current = null;
		}
	}, []);

	useEffect(() => {
		const verificationsArray = Object.values(verifications);

		if (verifyStatus === 'completed' && verificationsArray.length === 0) {
			if (!isCompletedRef.current && processId === currentProcessIdRef.current) {
				dispatch(processCompleted());
				isCompletedRef.current = true;
			}
			return;
		}

		if (!processId || verifyStatus !== 'completed') return;
		if (verificationsArray.length === 0) return;

		if (hasStartedRef.current) return;

		if (status === 'processing' || status === 'completed' || status === 'error') return;

		hasStartedRef.current = true;
		isMountedRef.current = true;
		isCompletedRef.current = false;
		currentProcessIdRef.current = processId;
		retryCountRef.current = 0;

		if (status === 'idle') {
			dispatch(respondPhaseStarted());
			dispatch(addTimelineEvent({ phase: 'respond', eventType: 'phase_start' }));
		}

		const connectSSE = (reviewId: string) => {
			if (!isMountedRef.current || isCompletedRef.current) return;

			const url = `${API_BASE}/suggest-actions?lang=${lang}&review_id=${encodeURIComponent(reviewId)}${refreshCache ? '&refresh_cache=true' : ''}`;

			try {
				const eventSource = new EventSource(url);
				eventSourceRef.current = eventSource;

				const resetConnectionTimeout = () => {
					if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
					connectionTimeoutRef.current = setTimeout(() => {
						if (isMountedRef.current && !isCompletedRef.current) {
							cleanup();
							const msg = 'Connection timeout during response generation';
							dispatch(addTimelineEvent({ phase: 'respond', eventType: 'error', message: msg }));
							dispatch(respondPhaseFailed(msg));
						}
					}, SSE_CONFIG.CONNECTION_TIMEOUT_MS);
				};
				resetConnectionTimeout();

				eventSource.addEventListener(SENTINEL_SSE_EVENTS.STATUS_CHANGE, (event) => {
					resetConnectionTimeout();
					try {
						const data = JSON.parse(event.data);
						const normalizedRole = normalizeRole(data.role);
						dispatch(
							addOrUpdateTimelineEvent({
								phase: 'respond',
								eventType: 'step_update',
								stepId: data.step_id,
								stepStatus: data.new_status,
								role: normalizedRole
							})
						);
					} catch (e) {
						console.error(e);
					}
				});

				eventSource.addEventListener('risk_analysis', (event) => {
					resetConnectionTimeout();
					try {
						dispatch(respondPhaseRiskAnalysisReceived(JSON.parse(event.data)));
					} catch (e) {
						console.error(e);
					}
				});

				eventSource.addEventListener('action_suggestions', (event) => {
					if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
					try {
						const data = JSON.parse(event.data);
						dispatch(
							addOrUpdateTimelineEvent({
								phase: 'respond',
								eventType: 'step_update',
								stepId: 'suggest_actions',
								stepStatus: 'completed',
								role: 'community-manager'
							})
						);
						dispatch(respondPhaseActionsReceived(data.action_suggestions));
						dispatch(addTimelineEvent({ phase: 'respond', eventType: 'phase_complete' }));
						dispatch(processCompleted());
						cleanup();
					} catch (e) {
						console.error(e);
					}
				});

				eventSource.addEventListener(SENTINEL_SSE_EVENTS.ERROR, (event) => {
					const errorMsg = extractSSEErrorMessage(event);
					dispatch(addTimelineEvent({ phase: 'respond', eventType: 'error', message: errorMsg }));
					dispatch(respondPhaseFailed(errorMsg));
					cleanup();
				});

				eventSource.onerror = () => {
					eventSource.close();
					if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
					if (isCompletedRef.current || !isMountedRef.current) return;
					if (retryCountRef.current < SSE_CONFIG.MAX_RETRIES) {
						retryCountRef.current++;
						retryTimeoutRef.current = setTimeout(() => connectSSE(reviewId), SSE_CONFIG.RETRY_DELAY_MS);
					} else {
						const msg = 'Connection lost';
						dispatch(addTimelineEvent({ phase: 'respond', eventType: 'error', message: msg }));
						dispatch(respondPhaseFailed(msg));
					}
				};
			} catch (err) {
				console.error('Failed to create EventSource', err);
				dispatch(respondPhaseFailed('Failed to initialize connection'));
			}
		};

		const startRespondPhase = async () => {
			try {
				abortControllerRef.current = new AbortController();

				const body = {
					claims: verificationsArray.map((v) => ({
						claim: v.claim,
						explanation: v.reasoning || ''
					}))
				};

				const postResponse = await fetch(`${API_BASE}/upload-evaluation`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(body),
					signal: abortControllerRef.current.signal
				});

				if (!postResponse.ok) throw new Error(`Failed: ${postResponse.status}`);
				const responseData = await postResponse.json();

				if (!isMountedRef.current) return;
				connectSSE(responseData.review_id);
			} catch (err) {
				if (err instanceof DOMException && err.name === 'AbortError') return;
				if (!isMountedRef.current) return;
				const errorMsg = err instanceof Error ? err.message : 'Unknown error';
				dispatch(addTimelineEvent({ phase: 'respond', eventType: 'error', message: errorMsg }));
				dispatch(respondPhaseFailed(errorMsg));
			}
		};

		startRespondPhase();

		return () => {
			cleanup();
			hasStartedRef.current = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [verifyStatus, processId, verifications, dispatch, cleanup, refreshCache]);

	return { disconnect: cleanup };
}
